import type { SwiperOptions } from 'swiper/types';

import { isNumeric } from '../microApps/common/utils/numberUtils';
import { BREAKPOINT_MAX_TO, BREAKPOINT_MIN_FROM, Browser, BrowserSize } from './browser';

export type CoopLayoutSize = 'full' | 'default';

export type CoopContentPageSize = CoopLayoutSize | 'narrow';

export type SwiperBreakpoints =
    | {
          [BREAKPOINT_MIN_FROM]: SwiperOptions; // mandatory "from 0px" breakpoint
          [index: number]: SwiperOptions;
      }
    | undefined;

export const CoopSwiperBreakpoints = {
    AREA_BRAND_CATEGORY: 'BrandCategory',

    AREA_BRAND_CATEGORY_LISTING: 'BrandCategoryListing',

    AREA_RECIPE: 'Recipe',

    AREA_NEW_RECIPE_LIST: 'NewRecipeList',

    /** This is used in React Swiper LISTS of product teasers */
    AREA_PRODUCT_LIST: 'ProductList',

    /** This is used in React Swiper LISTS of product teasers WITH BANNER */
    AREA_PRODUCT_LIST_WITH_BANNER: 'ProductListWithBanner',

    /** This is used in GRIDS of product teasers, that are using ItemGrid component */
    AREA_PRODUCT_GRID: 'ProductGrid',

    AREA_GROUPED_OFFERS_GRID: 'GroupedOffersGrid',

    /** This is used in GRIDS of NEW React recipe lisiting block, when not using swiper, less than 6, centered and useNarrow */
    AREA_RECIPE_LISTING_GRID: 'RecipeListingGrid',

    /** This is used in weekly menu recipes blockin swiper list */
    AREA_WEEKLY_MENU_LIST: 'WeeklyMenuList',

    AREA_PERSONAL_OFFERS_LIST: 'PersonalOffersList',

    AREA_TV_OFFERS_LIST: 'TVOffersList',

    AREA_FOOTER_GRID: 'FooterGrid',

    AREA_STORE_OFFERS_LIST: 'StoreOffersList',
} as const;

export const getBreakpointsFor = (
    area: keyof SwiperBreakpointsCollection,
    hasSideMenu: boolean,
    pageContentSize: CoopContentPageSize,
    layoutSize: CoopLayoutSize,
): SwiperBreakpoints => {
    const isViewportInconsistent = hasSideMenu || pageContentSize !== layoutSize; // this mean that page changes at SIDEBAR_HIDE_BREAKPOINT and .e. sidebar is hidden, so the breakpoints must also change

    const pageContentSizeOrFallback = pageContentSize || layoutSize;

    switch (pageContentSizeOrFallback) {
        case 'full':
            return FULL_WITH_SIDEMENU_BREAKPOINTS[area];
        case 'narrow':
            return isViewportInconsistent
                ? NARROW_WITH_SIDEMENU_BREAKPOINTS[area]
                : NARROW_WITHOUT_SIDEMENU_BREAKPOINTS[area];
        case 'default':
        default:
            return isViewportInconsistent
                ? DEFAULT_WITH_SIDEMENU_BREAKPOINTS[area]
                : DEFAULT_WITHOUT_SIDEMENU_BREAKPOINTS[area];
    }
};

export const getSwiperBreakpoint = (browserSize: BrowserSize) => {
    return Browser.getBreakpoint(browserSize).from; // we always operate on the "from" values what describing Swiper breakponts, never use "to"
};

// The breakpoint where sidebar navigation gets hidden
const SIDEBAR_HIDE_BREAKPOINT = getSwiperBreakpoint(BrowserSize.lg);

type CoopSwiperBreakpointsValues =
    (typeof CoopSwiperBreakpoints)[keyof typeof CoopSwiperBreakpoints];

export type SwiperBreakpointsCollection = {
    [key in CoopSwiperBreakpointsValues]: SwiperBreakpoints;
};

// I.e. Handla, Hallbarhet, Medlem, Bank
const FULL_WITH_SIDEMENU_BREAKPOINTS = {
    BrandCategory: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 2, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.lg)]: { slidesPerView: 4, slidesPerGroup: 4 },
    },
    BrandCategoryListing: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 2, slidesPerGroup: 2 },
    },
    Recipe: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 2.25, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 4.25, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.md)]: { slidesPerView: 4.25, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 6.25, slidesPerGroup: 6 },
    },
    ProductList: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1.25, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xxsm)]: { slidesPerView: 1.4, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2.25, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 2, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.md)]: { slidesPerView: 3, slidesPerGroup: 3 },
        900: { slidesPerView: 4, slidesPerGroup: 4 },
        [SIDEBAR_HIDE_BREAKPOINT]: { slidesPerView: 3, slidesPerGroup: 3 },
        1200: { slidesPerView: 4, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.xxlg)]: { slidesPerView: 6, slidesPerGroup: 6 },
    },
    ProductListWithBanner: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1.25, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xxsm)]: { slidesPerView: 1.4, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2.25, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 2, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.md)]: { slidesPerView: 3, slidesPerGroup: 3 },
        900: { slidesPerView: 2, slidesPerGroup: 2 },
        [SIDEBAR_HIDE_BREAKPOINT]: { slidesPerView: 1, slidesPerGroup: 1 },
        1200: { slidesPerView: 2, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.xxlg)]: { slidesPerView: 4, slidesPerGroup: 4 },
    },
    ProductGrid: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        900: { slidesPerView: 4, slidesPerGroup: 4 },
        [SIDEBAR_HIDE_BREAKPOINT]: { slidesPerView: 3, slidesPerGroup: 3 },
        1200: { slidesPerView: 4, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.xxlg)]: { slidesPerView: 6, slidesPerGroup: 6 },
    },
    GroupedOffersGrid: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.md)]: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.lg)]: { slidesPerView: 4, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 6, slidesPerGroup: 6 },
    },
    // same as NewProductList
    NewRecipeList: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1.5, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2.25, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        900: { slidesPerView: 4, slidesPerGroup: 4 },
        [SIDEBAR_HIDE_BREAKPOINT]: { slidesPerView: 3, slidesPerGroup: 3 },
        1200: { slidesPerView: 4, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.xxlg)]: { slidesPerView: 6, slidesPerGroup: 6 },
    },
    RecipeListingGrid: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 2, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.md)]: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 4, slidesPerGroup: 4 },
    },
    WeeklyMenuList: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1.1 },
        [getSwiperBreakpoint(BrowserSize.xxxsm)]: { slidesPerView: 1.5 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        900: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.lg)]: { slidesPerView: 3, slidesPerGroup: 3 },
        1200: { slidesPerView: 4, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.xxlg)]: { slidesPerView: 5, slidesPerGroup: 5 },
    },
    PersonalOffersList: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 2.25, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.md)]: { slidesPerView: 4, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.lg)]: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 4, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.xxlg)]: { slidesPerView: 5, slidesPerGroup: 5 },
    },
    TVOffersList: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1.5, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2 },
        [getSwiperBreakpoint(BrowserSize.md)]: { slidesPerView: 3 },
        900: { slidesPerView: 2 },
        [getSwiperBreakpoint(BrowserSize.lg)]: { slidesPerView: 3 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 4 },
    },
    FooterGrid: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.lg)]: { slidesPerView: 6, slidesPerGroup: 6 },
    },
    StoreOffersList: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xxsm)]: { slidesPerView: 2 },
        [getSwiperBreakpoint(BrowserSize.md)]: { slidesPerView: 2 },
        [getSwiperBreakpoint(BrowserSize.lg)]: { slidesPerView: 4 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 4 },
    },
} as const satisfies SwiperBreakpointsCollection;

// I.e. My Coop pages
const DEFAULT_WITH_SIDEMENU_BREAKPOINTS = {
    ...FULL_WITH_SIDEMENU_BREAKPOINTS,
    ProductList: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1.25, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xxsm)]: { slidesPerView: 1.4, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2.25, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        900: { slidesPerView: 4, slidesPerGroup: 4 },
        [SIDEBAR_HIDE_BREAKPOINT]: { slidesPerView: 3, slidesPerGroup: 3 },
        1200: { slidesPerView: 4, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 4, slidesPerGroup: 4 },
    },
    ProductGrid: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        900: { slidesPerView: 4, slidesPerGroup: 4 },
        [SIDEBAR_HIDE_BREAKPOINT]: { slidesPerView: 3, slidesPerGroup: 3 },
        1200: { slidesPerView: 4, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 4, slidesPerGroup: 4 },
    },
    // same as ProductList
    NewRecipeList: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1.5, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2.25, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        900: { slidesPerView: 4, slidesPerGroup: 4 },
        [SIDEBAR_HIDE_BREAKPOINT]: { slidesPerView: 3, slidesPerGroup: 3 },
        1200: { slidesPerView: 4, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 4, slidesPerGroup: 4 },
    },
    PersonalOffersList: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 2, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3 },
        [getSwiperBreakpoint(BrowserSize.md)]: { slidesPerView: 4 },
        [getSwiperBreakpoint(BrowserSize.lg)]: { slidesPerView: 3 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 4 },
    },
} as const satisfies SwiperBreakpointsCollection;

// I.e. Checkout, Coop start page, Butiker, Global Search results
const DEFAULT_WITHOUT_SIDEMENU_BREAKPOINTS = {
    ...FULL_WITH_SIDEMENU_BREAKPOINTS,
    ProductList: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1.25, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xxsm)]: { slidesPerView: 1.4, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2.25, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.lg)]: { slidesPerView: 4, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 6, slidesPerGroup: 6 },
    },
    ProductListWithBanner: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1.25, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xxsm)]: { slidesPerView: 1.4, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2.25, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        900: { slidesPerView: 2, slidesPerGroup: 2 },
        1200: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.xxlg)]: { slidesPerView: 4, slidesPerGroup: 4 },
    },
    // mostly same as NewProductList
    ProductGrid: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.lg)]: { slidesPerView: 4, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 6, slidesPerGroup: 6 },
    },
    // same as NewProductList
    NewRecipeList: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1.5, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2.25, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.lg)]: { slidesPerView: 4, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 6, slidesPerGroup: 6 },
    },
    Recipe: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 2.25, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3.25, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.md)]: { slidesPerView: 4.25, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.lg)]: { slidesPerView: 5.25, slidesPerGroup: 5 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 6.25, slidesPerGroup: 6 },
    },
    RecipeListingGrid: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 2, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.md)]: { slidesPerView: 4, slidesPerGroup: 4 },
        [getSwiperBreakpoint(BrowserSize.lg)]: { slidesPerView: 5, slidesPerGroup: 5 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 6, slidesPerGroup: 6 },
    },
    WeeklyMenuList: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1.1 },
        [getSwiperBreakpoint(BrowserSize.xxxsm)]: { slidesPerView: 1.5 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3 },
        900: { slidesPerView: 3 },
        [getSwiperBreakpoint(BrowserSize.lg)]: { slidesPerView: 4 },
        1200: { slidesPerView: 4 },
        [getSwiperBreakpoint(BrowserSize.xxlg)]: { slidesPerView: 5 },
    },
    PersonalOffersList: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xxsm)]: { slidesPerView: 2 },
        [getSwiperBreakpoint(BrowserSize.md)]: { slidesPerView: 2 },
        [getSwiperBreakpoint(BrowserSize.lg)]: { slidesPerView: 4 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 4 },
    },
} as const satisfies SwiperBreakpointsCollection;

// I.e Recipe details page
const NARROW_WITH_SIDEMENU_BREAKPOINTS = {
    ...FULL_WITH_SIDEMENU_BREAKPOINTS,
    ProductList: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1.25, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xxsm)]: { slidesPerView: 1.4, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2.25, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        900: { slidesPerView: 4, slidesPerGroup: 4 },
        [SIDEBAR_HIDE_BREAKPOINT]: { slidesPerView: 2, slidesPerGroup: 2 },
        1200: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 4, slidesPerGroup: 4 },
    },
    ProductGrid: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        900: { slidesPerView: 4, slidesPerGroup: 4 },
        [SIDEBAR_HIDE_BREAKPOINT]: { slidesPerView: 2, slidesPerGroup: 2 },
        1200: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 4, slidesPerGroup: 4 },
    },
    Recipe: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1.25, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xxsm)]: { slidesPerView: 1.4, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2.25, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
        900: { slidesPerView: 4, slidesPerGroup: 4 },
        [SIDEBAR_HIDE_BREAKPOINT]: { slidesPerView: 2, slidesPerGroup: 2 },
        1200: { slidesPerView: 3, slidesPerGroup: 3 },
        [getSwiperBreakpoint(BrowserSize.xlg)]: { slidesPerView: 4, slidesPerGroup: 4 },
    },
} as const satisfies SwiperBreakpointsCollection;

// I.e. Article page
const NARROW_WITHOUT_SIDEMENU_BREAKPOINTS = {
    ...NARROW_WITH_SIDEMENU_BREAKPOINTS,
    ProductList: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1.25, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xxsm)]: { slidesPerView: 1.4, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.xsm)]: { slidesPerView: 2.25, slidesPerGroup: 2 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
    },
    ProductGrid: {
        [BREAKPOINT_MIN_FROM]: { slidesPerView: 1, slidesPerGroup: 1 },
        [getSwiperBreakpoint(BrowserSize.sm)]: { slidesPerView: 3, slidesPerGroup: 3 },
    },
} as const satisfies SwiperBreakpointsCollection;

interface BreakpointRange {
    from: number;
    to: number;
    cellsInRow: number;
}

export const getGridBreakpointRanges = (breakpointArray: SwiperBreakpoints): BreakpointRange[] => {
    const breakpoints =
        breakpointArray &&
        Object.entries(breakpointArray).map((x) => {
            const [key, value] = x;
            return {
                from: parseInt(key, 10),
                perView: isNumeric(value.slidesPerView) ? value.slidesPerView : 0,
            };
        });

    if (!breakpoints?.length)
        throw new Error('Breakpoint array must contain at least one breakpoint');

    const ranges: BreakpointRange[] = [];

    for (let i = 0; i < breakpoints.length - 1; i += 1) {
        ranges.push({
            from: breakpoints[i].from,
            to: breakpoints[i + 1].from - 1,
            cellsInRow: breakpoints[i].perView,
        });
    }

    // create last breakpoint from X to 99999
    ranges.push({
        from: breakpoints[breakpoints.length - 1].from,
        to: BREAKPOINT_MAX_TO,
        cellsInRow: breakpoints[breakpoints.length - 1].perView,
    });

    return ranges;
};
